import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Chip,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  TextField,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import { useNotify } from '../hooks';
import { useIsMobile } from '../utils/responsive';
import { pickProcessTypeHref, productionProductLines, readStoredProcessTypeHref, writeStoredProcessTypeHref } from './productionUi.mjs';

function formatWeight(weight, unit) {
  if (unit === 'g') return `${Math.round(weight)} г`;
  if (unit === 'kg') return `${weight.toFixed(3)} кг`;
  return `${weight} ${unit || ''}`.trim();
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(String(dateStr).replace(' ', 'T'));
    if (isNaN(d.getTime())) return dateStr;
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return dateStr;
  }
}

export default function ScaleLive() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError } = useNotify();
  const { user } = useSelector((state) => state.auth);

  const [data, setData] = useState({ weight: 0, unit: 'kg', stable: false, timestamp: null });
  const [types, setTypes] = useState([]);
  const [selectedTypeHref, setSelectedTypeHref] = useState('');
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState('list');
  const [currentTask, setCurrentTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState({ stages: [], products: [], loading: false });
  const esRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCtx, setConfirmCtx] = useState({ stageId: null, productName: '', productId: null, weightKg: 0, manualWeight: '', quantity: 1 });
  const [loading, setLoading] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialsMeta, setMaterialsMeta] = useState({ ok: true, quantity: 1 });
  // EventSource для весов
  useEffect(() => {
    const token = localStorage.getItem('token');
    const base = axios.defaults.baseURL || (import.meta.env.VITE_API_URL || '');
    const url = `${base}/scale/stream?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url, { withCredentials: false });
    esRef.current = es;

    es.addEventListener('update', (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        setData(payload);
      } catch (e) {
        // ignore
      }
    });

    es.addEventListener('ping', () => {});

    es.onerror = () => {
      // Браузер сам переподключает EventSource
    };

    return () => {
      es.close();
    };
  }, []);

  // Загрузка типов производства
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await axios.get('/ms/process-types');
        const list = res.data?.types || [];
        setTypes(list);
        setSelectedTypeHref((current) => pickProcessTypeHref(list, {
          storedHref: readStoredProcessTypeHref(),
          taskHref: current,
        }) || current);
      } catch (e) {
        // ignore
      }
    };
    loadTypes();
  }, []);

  const fetchTasks = async () => {
    if (!selectedTypeHref) return;
    try {
      setLoading(true);
      const res = await axios.get('/ms/production-tasks/list', { params: { typeHref: selectedTypeHref } });
      setTasks(Array.isArray(res.data?.tasks) ? res.data.tasks : []);
    } catch (e) { setTasks([]); }
    finally { setLoading(false); }
  };

  const onCheckMaterials = async (stageRow, productRow) => {
    // Проверка прав доступа
    const hasPermission = user?.role === 'admin' || 
                         user?.permissions?.scale === true ||
                         user?.permissions?.vacuum === true || 
                         user?.permissions?.production === true;
    
    if (!hasPermission) {
      showError('Недостаточно прав. Для проверки остатков требуется доступ к "Весы", "Производство" или "Вакуум"');
      return;
    }

    try {
      if (!productRow?.planId) return;
      setMaterialsOpen(true);
      setMaterialsLoading(true);
      setMaterials([]);
      const qty = Math.max(1, Number(stageRow.totalQuantity || 0) - Number(stageRow.completedQuantity || 0));
      const qp = new URLSearchParams({ planId: productRow.planId, quantity: String(qty || 1) }).toString();
      const { data } = await axios.get(`/ms/vacuum/check-materials?${qp}`);
      const list = Array.isArray(data?.items) ? data.items : [];
      setMaterials(list);
      setMaterialsMeta({ ok: Boolean(data?.ok), quantity: Number(data?.quantity || qty) });
    } catch (e) {
      setMaterials([]);
      setMaterialsMeta({ ok: false, quantity: 1 });
      setMaterialsOpen(true);
      const errorMsg = e?.response?.data?.message || 'Ошибка проверки материалов';
      showError(errorMsg);
    } finally {
      setMaterialsLoading(false);
    }
  };

  const openConfirm = (stageId, productName, productId) => {
    const weightKg = data.unit === 'kg' ? Number(data.weight) : Number((Number(data.weight) / 1000).toFixed(3));
    setConfirmCtx({ stageId, productName: productName || '', productId: productId || null, weightKg, manualWeight: '', quantity: 1 });
    setConfirmOpen(true);
  };

  const openTask = async (task) => {
    setCurrentTask(task);
    setView('details');
    try {
      const params = new URLSearchParams(location.search);
      params.set('task', task.id);
      navigate({ search: params.toString() }, { replace: false });
    } catch (_) {}
    await reloadDetails(task.id);
  };

  const reloadDetails = async (taskId) => {
    try {
      setTaskDetails(s => ({ ...s, loading: true }));
      const { data } = await axios.get(`/ms/production-tasks/${taskId}/details`);
      const stages = (data?.stages || []).filter(s => Number(s.totalQuantity) !== Number(s.completedQuantity));
      setTaskDetails({ stages, products: data?.products || [], loading: false });
      if (data?.processTypeHref) {
        setSelectedTypeHref(data.processTypeHref);
        writeStoredProcessTypeHref(data.processTypeHref);
      }
    } catch (_) {
      setTaskDetails({ stages: [], products: [], loading: false });
    }
  };

  const backToList = async () => {
    setView('list');
    setCurrentTask(null);
    setTaskDetails({ stages: [], products: [], loading: false });
    try {
      const params = new URLSearchParams(location.search);
      params.delete('task');
      navigate({ search: params.toString() }, { replace: false });
    } catch (_) {}
    await fetchTasks();
  };

  const handleProduce = async () => {
    try {
      const manual = parseFloat(String(confirmCtx.manualWeight || '').replace(',', '.'));
      const weightToSend = !isNaN(manual) && manual > 0 ? Number(manual.toFixed(3)) : Number(confirmCtx.weightKg.toFixed(3));
      const payload = {
        stageId: confirmCtx.stageId,
        weightKg: weightToSend,
        processTypeHref: selectedTypeHref || undefined,
        productId: confirmCtx.productId || undefined,
        quantity: Math.max(1, Number(confirmCtx.quantity || 1)),
      };
      await axios.post('/ms/complete-stage', payload);
      setConfirmOpen(false);
      showSuccess('Этап произведён успешно');
      if (currentTask?.id) await reloadDetails(currentTask.id);
    } catch (e) {
      setConfirmOpen(false);
      const msg = e?.response?.data?.message || e?.message || 'Ошибка выполнения операции';
      showError(msg);
    }
  };

  // Авто-открытие задания по параметру ?task=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    if (!taskId) return;
    if (view !== 'details' || String(currentTask?.id || '') !== String(taskId)) {
      setCurrentTask({ id: taskId });
      setView('details');
      reloadDetails(taskId);
    }
  }, [location.search]);


  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Производство (Весы)
      </Typography>

      {/* Карточка с весом */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', md: '3rem' } }}>
                {formatWeight(data.weight, data.unit)}
              </Typography>
              <Chip label={data.stable ? 'Стабильно' : 'Нестабильно'} color={data.stable ? 'success' : 'warning'} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: '100%', sm: 'auto' }, flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Тип производства</InputLabel>
                <Select
                  label="Тип производства"
                  value={selectedTypeHref}
                  onChange={(e) => {
                    setSelectedTypeHref(e.target.value);
                    writeStoredProcessTypeHref(e.target.value);
                  }}
                >
                  {!selectedTypeHref ? <MenuItem value="">Выберите тип</MenuItem> : null}
                  {types.map((t) => (
                    <MenuItem key={t.id} value={t.href}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={fetchTasks}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'fit-content' }}
              >
                {loading ? 'Загрузка…' : 'Получить задания'}
              </Button>
            </Box>
          </Box>
          {data.timestamp && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              обновлено: {formatDate(new Date(data.timestamp).toISOString())}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Список заданий */}
      {view === 'list' && (
        isMobile ? (
          <Stack spacing={1.5}>
            {tasks.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                {loading ? 'Загрузка...' : 'Нет заданий. Выберите тип и нажмите "Получить задания"'}
              </Paper>
            ) : (
              tasks.map((t) => (
                <Card key={t.id} variant="outlined">
                  <CardActionArea onClick={() => openTask(t)}>
                    <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{t.name || t.id}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(t.moment)}</Typography>
                      </Box>
                      {t.description && (
                        <Typography variant="body2" color="text.secondary">
                          {t.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
            )}
          </Stack>
        ) : (
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Задание</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Комментарий</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {loading ? 'Загрузка...' : 'Нет заданий. Выберите тип и нажмите "Получить задания"'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((t) => (
                      <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTask(t)}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.name || t.id}</TableCell>
                        <TableCell>{formatDate(t.moment)}</TableCell>
                        <TableCell>{t.description || ''}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      )}

      {/* Детали задания */}
      {view === 'details' && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">{currentTask?.name || currentTask?.id}</Typography>
            <Button variant="outlined" onClick={backToList}>Назад</Button>
          </Box>
          <Typography variant="body2" color="text.secondary">Дата: {formatDate(currentTask?.moment)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Комментарий: {currentTask?.description || '—'}</Typography>

          {taskDetails.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : taskDetails.stages.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>Все этапы выполнены</Typography>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {taskDetails.stages.map((st) => {
                const prod = (taskDetails.products || []).find(p => p.productionRowHref === st.productionRowHref);
                const productLines = productionProductLines(prod);
                return (
                  <Card key={st.id} variant="outlined">
                    <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Box onClick={() => onCheckMaterials(st, prod)} sx={{ cursor: 'pointer', pr: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {productLines.name}
                          </Typography>
                          {productLines.color ? (
                            <Typography variant="body2" color="text.secondary">{productLines.color}</Typography>
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {st.totalQuantity} / {st.completedQuantity}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        onClick={() => openConfirm(st.id, prod?.productName, prod?.productId)}
                        sx={{ mt: 1 }}
                      >
                        Произвести
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Товар</TableCell>
                    <TableCell align="right">Всего / Выполнено</TableCell>
                    <TableCell align="right">Действие</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskDetails.stages.map((st) => {
                    const prod = (taskDetails.products || []).find(p => p.productionRowHref === st.productionRowHref);
                    const productLines = productionProductLines(prod);
                    return (
                      <TableRow key={st.id} hover>
                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => onCheckMaterials(st, prod)}>
                          <Typography variant="body2">{productLines.name}</Typography>
                          {productLines.color ? (
                            <Typography variant="caption" color="text.secondary" display="block">{productLines.color}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{`${st.totalQuantity} / ${st.completedQuantity}`}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="contained" onClick={() => openConfirm(st.id, prod?.productName, prod?.productId)}>Произвести</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Диалог подтверждения */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Произвести?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>Товар: {confirmCtx.productName || '—'}</Typography>
          <Typography sx={{ mt: 1 }}>Вес (фикс.): {confirmCtx.weightKg.toFixed(3)} кг</Typography>
          <TextField
            margin="dense"
            label="Вес вручную, кг"
            type="number"
            fullWidth
            value={confirmCtx.manualWeight}
            onChange={(e) => setConfirmCtx({ ...confirmCtx, manualWeight: e.target.value })}
            placeholder="Например, 1.234"
            inputProps={{ step: '0.001', min: '0' }}
          />
          <TextField
            margin="dense"
            label="Количество"
            type="number"
            fullWidth
            value={confirmCtx.quantity}
            onChange={(e) => setConfirmCtx({ ...confirmCtx, quantity: Math.max(1, Number(e.target.value || 1)) })}
            inputProps={{ min: '1' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Нет</Button>
          <Button variant="contained" onClick={handleProduce}>Да</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог материалов */}
      <Dialog open={materialsOpen} onClose={() => setMaterialsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Проверка материалов (qty: {materialsMeta.quantity})</DialogTitle>
        <DialogContent dividers>
          {materialsLoading ? (
            <Box sx={{ p: 1 }}><CircularProgress size={18} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Материал</TableCell>
                  <TableCell align="right">Нужно</TableCell>
                  <TableCell align="right">Есть</TableCell>
                  <TableCell align="right">Дефицит</TableCell>
                  <TableCell>Группа</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materials.map((m, idx) => (
                  <TableRow key={idx} sx={{ bgcolor: m.shortage > 0 ? 'error.lighter' : undefined }}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell align="right">{m.need}</TableCell>
                    <TableCell align="right">{m.inStock}</TableCell>
                    <TableCell align="right" sx={{ color: m.shortage > 0 ? 'error.main' : undefined }}>{m.shortage}</TableCell>
                    <TableCell>{m.groupName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color={materialsMeta.ok ? 'success.main' : 'error.main'}>
              {materialsMeta.ok ? 'Материалов достаточно' : 'Есть дефицит материалов'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaterialsOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
